import React, { useEffect, useState } from "react"
import { MdOpenInNew, MdOutlineDelete, MdOutlineInbox, MdRefresh } from "react-icons/md"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"
import api from "../../services/api"
import Button from "../Form/Button"
import Input from "../Form/Input"
import Select from "../Form/Select"
import Modal from "./index"
import ModalConfirm from "./ModalConfirm"
import styles from "../../pages/ApiIntegration/apiIntegration.module.css"

const statusOptions = [
    { id: "all", value: "Todos os status" },
    { id: "pending_config", value: "Aguardando configuração" },
    { id: "processing", value: "Processando" },
    { id: "imported", value: "Importado" },
    { id: "duplicate_blocked", value: "Duplicidade bloqueada" },
    { id: "failed", value: "Falhou" },
    { id: "discarded", value: "Descartado" },
]

const statusLabels = Object.fromEntries(statusOptions.filter(option => option.id !== "all").map(option => [option.id, option.value]))
const formatDateTime = date => date ? new Date(date).toLocaleString("pt-BR") : "-"

const findLeadValue = (lead = {}, patterns = []) => {
    const entry = Object.entries(lead).find(([key, value]) => value && patterns.some(pattern => key.toLowerCase().includes(pattern)))
    return entry?.[1]
}

const getSubmissionIdentity = (lead = {}) => {
    const name = findLeadValue(lead, ["full_name", "name", "nome"])
    const email = findLeadValue(lead, ["email", "e-mail"])
    const phone = findLeadValue(lead, ["phone", "telefone", "celular", "whatsapp"])
    const fallback = Object.values(lead).find(value => value !== undefined && value !== null && value !== "")
    return { title: name || email || phone || fallback || "Cadastro sem identificação", detail: name ? (email || phone) : "" }
}

const KeyValueList = ({ values = {} }) => {
    const entries = Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== "" && !(typeof value === "object" && !Object.keys(value).length))
    if (!entries.length) return <div className={styles.submissionEmptySection}>Nenhuma informação recebida.</div>
    return (
        <dl className={styles.submissionKeyValueList}>
            {entries.map(([key, value]) => (
                <div key={key}>
                    <dt>{key}</dt>
                    <dd>{typeof value === "object" ? JSON.stringify(value) : String(value)}</dd>
                </div>
            ))}
        </dl>
    )
}

const ModalMetaLeadgenSubmissions = ({ page = {}, closeModal = () => {}, onChanged = () => {} }) => {
    const { account_id } = useParams()
    const [submissions, setSubmissions] = useState([])
    const [pagination, setPagination] = useState({})
    const [status, setStatus] = useState("all")
    const [forms, setForms] = useState([])
    const [selectedForms, setSelectedForms] = useState([])
    const [search, setSearch] = useState("")
    const [appliedSearch, setAppliedSearch] = useState("")
    const [selected, setSelected] = useState(null)
    const [loading, setLoading] = useState(false)
    const [actionLoading, setActionLoading] = useState(false)
    const [submissionToDiscard, setSubmissionToDiscard] = useState(null)

    const getForms = async () => {
        try {
            const response = await api.get(`/integrations/meta/accounts/${page._id}/forms`)
            const data = response?.data?.data?.data || []
            setForms(data.map(form => ({ id: form.id, value: form.name || form.id })))
        } catch (_) {
            setForms([])
        }
    }

    const getSubmissions = async (pageNumber = 1, append = false) => {
        try {
            setLoading(true)
            const response = await api.get(`/integrations/meta/accounts/${page._id}/submissions`, {
                params: { status, search: appliedSearch, forms: selectedForms.join(","), page: pageNumber, limit: 25 }
            })
            const data = response?.data?.data || []
            setSubmissions(previous => append ? [...previous, ...data] : data)
            setPagination(response?.data?.pagination || {})
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível carregar os cadastros.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { getForms() }, [])
    useEffect(() => {
        setSelected(null)
        getSubmissions()
    }, [status, appliedSearch, selectedForms])

    const openSubmission = async submissionId => {
        try {
            setLoading(true)
            const response = await api.get(`/integrations/meta/accounts/${page._id}/submissions/${submissionId}`)
            setSelected(response?.data?.data || null)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível abrir o cadastro.")
        } finally {
            setLoading(false)
        }
    }

    const refreshAfterAction = async submission => {
        await getSubmissions()
        await openSubmission(submission._id)
        onChanged()
    }

    const discardSubmission = async () => {
        if (!submissionToDiscard) return
        const submissionId = submissionToDiscard._id
        setSubmissionToDiscard(null)
        try {
            setActionLoading(true)
            const response = await api.patch(`/integrations/meta/accounts/${page._id}/submissions/${submissionId}/discard`)
            toast.success(response?.data?.message)
            await refreshAfterAction(response?.data?.data)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível descartar o cadastro.")
        } finally {
            setActionLoading(false)
        }
    }

    const retrySubmission = async () => {
        if (!selected) return
        try {
            setActionLoading(true)
            const response = await api.post(`/integrations/meta/accounts/${page._id}/submissions/${selected._id}/retry`)
            toast.success(response?.data?.message)
            await refreshAfterAction(response?.data?.data)
        } catch (err) {
            const updated = err?.response?.data?.data
            if (updated) await refreshAfterAction(updated)
            toast.error(err?.response?.data?.message || "Não foi possível reprocessar o cadastro.")
        } finally {
            setActionLoading(false)
        }
    }

    return (
        <>
            <Modal icon={<MdOutlineInbox />} title="Cadastros recebidos" description={`Página ${page.page_name || ""}`} closeModal={closeModal} fullWidith={true}>
                <div className={`${styles.submissionsLayout} ${styles.metaSubmissionsLayout}`}>
                    <section className={styles.submissionsListPanel}>
                        <div className={`${styles.submissionsToolbar} ${styles.metaSubmissionsToolbar}`}>
                            <div className={styles.submissionsSearch}>
                                <Input type="search" placeholder="Buscar nome, e-mail, telefone..." value={search} onChange={event => setSearch(event.target.value)} onKeyDown={event => event.key === "Enter" && setAppliedSearch(search.trim())} />
                                <Button variant="secondary" size="sm" fullWidth={false} onClick={() => setAppliedSearch(search.trim())}>Buscar</Button>
                            </div>
                            <Select name="Status" placeholder="Filtrar status" options={statusOptions} selected={status} multiple={false} allowDeselectAll={false} onChange={setStatus} />
                            <Select name="Formulários" placeholder="Todos os formulários" options={forms} selected={selectedForms} multiple={true} onChange={setSelectedForms} />
                        </div>
                        <div className={styles.submissionsCount}>{Number(pagination.total || 0).toLocaleString("pt-BR")} cadastros encontrados</div>
                        <div className={styles.submissionsList}>
                            {submissions.map(submission => {
                                const identity = getSubmissionIdentity(submission.lead)
                                return (
                                    <button key={submission._id} type="button" className={`${styles.submissionListItem} ${selected?._id === submission._id ? styles.active : ""}`} onClick={() => openSubmission(submission._id)}>
                                        <div><strong>{identity.title}</strong>{identity.detail && <span>{identity.detail}</span>}<small>{submission.form_name || submission.form_id} • {formatDateTime(submission.created_at)}</small></div>
                                        <span className={`${styles.submissionStatus} ${styles[submission.status]}`}>{statusLabels[submission.status] || submission.status}</span>
                                    </button>
                                )
                            })}
                            {!submissions.length && !loading && <div className={styles.submissionsEmpty}>Nenhum cadastro encontrado com esses filtros.</div>}
                            {loading && <div className={styles.submissionsEmpty}>Carregando cadastros...</div>}
                        </div>
                        {pagination.has_more && <Button variant="secondary" onClick={() => getSubmissions(Number(pagination.page || 1) + 1, true)} disabled={loading}>Carregar mais</Button>}
                    </section>
                    <section className={styles.submissionDetailPanel}>
                        {!selected && <div className={styles.submissionDetailEmpty}><MdOutlineInbox /><strong>Selecione um cadastro</strong><span>Veja os dados enviados pelo Meta e o status da importação.</span></div>}
                        {selected && <>
                            <div className={styles.submissionDetailHeader}>
                                <div><span className={`${styles.submissionStatus} ${styles[selected.status]}`}>{statusLabels[selected.status] || selected.status}</span><small>Recebido em {formatDateTime(selected.created_at)}</small></div>
                                <div className={styles.submissionActions}>
                                    {selected.status === "imported" && selected.lead_id && <a className={styles.submissionLeadLink} href={`/${account_id}/crm?openModal=1&leadId=${selected.lead_id}`} target="_blank" rel="noreferrer">Abrir lead <MdOpenInNew /></a>}
                                    {["duplicate_blocked", "failed"].includes(selected.status) && <Button variant="secondary" size="sm" fullWidth={false} onClick={retrySubmission} disabled={actionLoading}><MdRefresh /> Tentar novamente</Button>}
                                    {["pending_config", "duplicate_blocked", "failed"].includes(selected.status) && <Button variant="danger" size="sm" fullWidth={false} onClick={() => setSubmissionToDiscard(selected)} disabled={actionLoading}>Descartar</Button>}
                                </div>
                            </div>
                            {selected.error_message && <div className={selected.status === "duplicate_blocked" ? styles.submissionWarning : styles.submissionError}><strong>{selected.status === "duplicate_blocked" ? "Duplicidade bloqueada" : "Falha na importação"}</strong><span>{selected.error_message}</span></div>}
                            <div className={styles.submissionSection}><h3>Dados do cadastro</h3><KeyValueList values={selected.lead} /></div>
                            <div className={styles.submissionSection}><h3>Origem e campanha</h3><KeyValueList values={selected.campaign} /></div>
                            <details className={styles.submissionTechnical}><summary>Dados técnicos</summary><KeyValueList values={selected.technical} /></details>
                        </>}
                    </section>
                </div>
            </Modal>
            {submissionToDiscard && <ModalConfirm icon={<MdOutlineDelete />} title="Descartar cadastro?" description="Este cadastro não será enviado ao Kanban." text="Os dados continuarão disponíveis no histórico com o status Descartado." confirmLabel="Descartar" confirmVariant="danger" closeModal={() => setSubmissionToDiscard(null)} onSubmit={discardSubmission} />}
        </>
    )
}

export default ModalMetaLeadgenSubmissions
